import { asCardFoil } from '../types/foil'
import { parseDeckList } from '../engine/deckValidation'
import type { DeckList } from '../types/protocol'

export const SAVED_DECK_LIMIT = 10
export const DECK_NAME_MAX = 64
export const DECK_ID_MAX = 64

export type SavedDeckSlot = {
  id: string
  name: string
  main: string[]
  extra: string[]
  mainFoils: string[]
  extraFoils: string[]
}

const ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/**
 * True when this string can be a saved-deck id.
 */
export function isSavedDeckId(raw: unknown): raw is string {
  return typeof raw === 'string' && raw.length > 0 && raw.length <= DECK_ID_MAX
    && ID_RE.test(raw)
}

function clipName(raw: unknown): string {
  if (typeof raw !== 'string') return 'My Yu-Gi-Oh Deck'
  const name = raw.replace(/\s+/g, ' ').trim().slice(0, DECK_NAME_MAX)
  return name.length > 0 ? name : 'My Yu-Gi-Oh Deck'
}

/**
 * One slot from a PUT body. Null when the shape is unusable.
 */
export function parseSavedDeckSlot(raw: unknown): SavedDeckSlot | null {
  if (!raw || typeof raw !== 'object') return null
  const rec = raw as Record<string, unknown>
  if (!isSavedDeckId(rec.id)) return null
  const list: DeckList | null = parseDeckList({
    main: rec.main ?? rec.mainDeck,
    extra: rec.extra ?? rec.extraDeck,
    mainFoils: rec.mainFoils,
    extraFoils: rec.extraFoils,
  })
  if (!list) return null
  return {
    id: rec.id,
    name: clipName(rec.name),
    main: list.main,
    extra: list.extra,
    mainFoils: (list.mainFoils ?? []).map((foil: string) => asCardFoil(foil)),
    extraFoils: (list.extraFoils ?? []).map((foil: string) => asCardFoil(foil)),
  }
}

/**
 * Up to SAVED_DECK_LIMIT unique slots.
 */
export function parseSavedDeckList(raw: unknown): SavedDeckSlot[] | null {
  if (!Array.isArray(raw)) return null
  const used = new Set<string>()
  const slots: SavedDeckSlot[] = []
  for (const item of raw.slice(0, SAVED_DECK_LIMIT)) {
    const slot = parseSavedDeckSlot(item)
    if (!slot || used.has(slot.id)) continue
    used.add(slot.id)
    slots.push(slot)
  }
  return slots
}
